/**
 * Página de pago pendiente del checkout AR
 * 
 * Cuando el pago está pendiente de aprobación (efectivo, transferencia, etc.)
 */

import { useNavigate, useSearchParams } from "react-router-dom";
import { Clock, Home, UserPlus, ArrowRight } from "lucide-react";
import { BRAND_PERSON_NAME } from "@/lib/brandConfig";
import { MEMBERSHIP_ROUTE } from "@/lib/membershipAccessFlow";

/** Font iOS (San Francisco) */
const FONT_FAMILY =
  '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", "Helvetica Neue", Helvetica, Arial, sans-serif';

export default function InvitacionArPending() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Construir URL de registro con los identificadores disponibles
  const registroUrl = (() => {
    const parts = ["from_mp=true", "plan_tier=vip"];
    const paymentId = searchParams.get("payment_id");
    const subscriptionId = searchParams.get("subscription_id") || searchParams.get("preapproval_id");
    const identifier = subscriptionId || paymentId;
    if (identifier) parts.push(`subscription_id=${encodeURIComponent(identifier)}`);
    return `${MEMBERSHIP_ROUTE.REGISTRO}?${parts.join("&")}`;
  })();

  return (
    <div
      className="min-h-screen bg-black text-white flex items-center justify-center px-4"
      style={{ fontFamily: FONT_FAMILY }}
    >
      {/* Fondo con gradiente */}
      <div className="fixed inset-0 -z-10 bg-black">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500 via-amber-500 to-orange-500 opacity-20" />
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-transparent via-black/50 to-black" />
      </div>

      <div className="max-w-md w-full text-center space-y-6">
        {/* Icono de pendiente */}
        <div className="relative inline-flex justify-center w-full">
          <div className="absolute inset-0 blur-2xl bg-yellow-500/40 animate-pulse" />
          <div className="relative w-24 h-24 bg-gradient-to-br from-yellow-500 to-amber-500 rounded-full flex items-center justify-center shadow-2xl">
            <Clock className="w-12 h-12 text-white" />
          </div>
        </div>

        {/* Título */}
        <h1 className="text-3xl font-bold bg-gradient-to-r from-yellow-400 to-amber-400 bg-clip-text text-transparent">
          Pago pendiente
        </h1>

        {/* Mensaje */}
        <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
          <p className="text-gray-300 text-base leading-relaxed mb-4">
            Tu pago está siendo procesado por Mercado Pago. Si pagaste en efectivo o por
            transferencia, la acreditación puede demorar hasta 24-48 hs hábiles.
          </p>
          <p className="text-gray-400 text-sm">
            Una vez confirmado, el equipo de {BRAND_PERSON_NAME} activará tu acceso.
          </p>
        </div>

        {/* CTA principal: crear cuenta ahora (sin esperar la confirmación) */}
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-2xl p-5 space-y-3">
          <p className="text-amber-200 text-sm font-medium">
            ✅ Podés crear tu cuenta ahora
          </p>
          <p className="text-amber-100/75 text-xs leading-relaxed">
            No tenés que esperar la confirmación del pago para registrarte.
            Cuando el pago sea acreditado, el equipo activará tu acceso desde el panel de administración.
          </p>
          <a
            href={registroUrl}
            className="w-full py-4 px-6 bg-gradient-to-r from-yellow-500 to-amber-500 rounded-xl font-bold text-white shadow-lg hover:shadow-2xl transform hover:scale-105 active:scale-95 ui-transition duration-200 flex items-center justify-center gap-2"
          >
            <UserPlus className="w-5 h-5" />
            <span>Crear mi cuenta</span>
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        {/* Botón secundario */}
        <button
          onClick={() => navigate("/")}
          className="w-full py-3 px-6 rounded-xl border border-white/15 text-gray-400 text-sm font-medium hover:border-white/30 hover:text-white transition-colors duration-200 flex items-center justify-center gap-2"
        >
          <Home className="w-4 h-4" />
          <span>Volver al inicio</span>
        </button>

        {/* Footer */}
        <p className="text-gray-600 text-xs">
          Tu acceso se activará una vez confirmado el pago. Revisá tu email de Mercado Pago.
        </p>
      </div>
    </div>
  );
}
